import { useQuery } from '@tanstack/react-query';
import { Video, Users, Clock, HardDrive, DollarSign, Activity, Film } from 'lucide-react';
import StatCard from '@/components/StatCard';
import { analyticsApi } from '@/features/analytics/analytics.api';
import { useAuthContext } from '@/features/auth/AuthProvider';

function formatBytes(b: number): string {
  if (!b) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let n = b;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(1)} ${u[i]}`;
}

export default function AnalyticsPage(): JSX.Element {
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';

  const me = useQuery({ queryKey: ['analytics', 'me'], queryFn: () => analyticsApi.me() });
  const overview = useQuery({ queryKey: ['analytics', 'overview'], queryFn: () => analyticsApi.overview(), enabled: isAdmin });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold">Analytics</h1>
        <p className="text-sm text-slate-500">Your meeting activity</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon={Video} label="Meetings hosted" value={me.data?.hostedMeetings ?? '—'} />
          <StatCard icon={Clock} label="Total minutes" value={me.data?.totalMinutes ?? '—'} />
          <StatCard icon={Users} label="Participants hosted" value={me.data?.totalParticipantsHosted ?? '—'} />
          <StatCard icon={Film} label="Recordings" value={me.data?.recordings ?? '—'} />
        </div>
      </div>

      {isAdmin && (
        <div>
          <h2 className="text-lg font-semibold">Platform overview</h2>
          <p className="text-sm text-slate-500">Admin-only metrics</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard icon={Video} label="Total meetings" value={overview.data?.meetings.total ?? '—'} hint={`${overview.data?.meetings.live ?? 0} live now`} />
            <StatCard icon={Users} label="Users" value={overview.data?.users.total ?? '—'} hint={`${overview.data?.users.newLast30d ?? 0} new in 30d`} />
            <StatCard icon={Activity} label="Avg participants" value={overview.data?.engagement.avgParticipantsPerMeeting ?? '—'} hint="per meeting" />
            <StatCard icon={Clock} label="Total minutes" value={overview.data?.engagement.totalMinutes ?? '—'} />
            <StatCard icon={HardDrive} label="Recording storage" value={formatBytes(overview.data?.storage.totalBytes ?? 0)} hint={`${overview.data?.storage.recordings ?? 0} files`} />
            <StatCard icon={DollarSign} label="Revenue" value={`$${((overview.data?.revenue.totalPaidCents ?? 0) / 100).toFixed(2)}`} hint="total paid" />
          </div>

          {overview.data && overview.data.revenue.byPlan.length > 0 && (
            <div className="card mt-4">
              <h3 className="mb-3 font-semibold">Subscriptions by plan</h3>
              <div className="space-y-2">
                {overview.data.revenue.byPlan.map((p) => {
                  const max = Math.max(...overview.data!.revenue.byPlan.map((x) => x.count), 1);
                  return (
                    <div key={p.tier} className="flex items-center gap-3 text-sm">
                      <span className="w-24 text-slate-500">{p.tier}</span>
                      <div className="h-3 flex-1 rounded-full bg-slate-100 dark:bg-slate-800">
                        <div className="h-3 rounded-full bg-brand-600" style={{ width: `${(p.count / max) * 100}%` }} />
                      </div>
                      <span className="w-8 text-right">{p.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
